import { CommonModule, DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Permissions } from '@app/core/guard/Permissions';
import {
  ClientContractListItemResponse,
  ClientContractResponse,
} from '@interfaces/companys/contracts/responses/client-contract.response';
import { ConfirmationService } from '@services/helpers/confirmation.service';
import { PermissionsService } from '@services/helpers/permissions.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { ClientContractsHttpService } from '@services/http/companys/client-contracts-http.service';
import { saveBlobAsFile } from '@src/app/core/utils/zip-download.utils';
import { ColumnDefinition } from '@src/app/shared/interfaces/column-definition.interface';
import { SharedModule } from '@src/app/shared/shared.module';
import { MenuItem } from 'primeng/api';
import { finalize } from 'rxjs/operators';
import { backendErrorMessage } from '../../../../partner/utils/partner-contract.utils';
import {
  clientContractStatusLabel,
  clientContractTypeLabel,
  isClientContractFinalized,
} from '../../utils/client-contract.utils';
import { ModalGenerateClientContractComponent } from './components/modal-generate-client-contract/modal-generate-client-contract.component';

/**
 * Cartão "Contratos" do cadastro da empresa (card 3643): os contratos de cliente (Flat Fee e Gestão
 * Ativa) gerados para ela e o botão "Gerar contrato". Abrir uma linha leva ao editor
 * (`company/contracts/:id`). O pai só monta este componente em edição e com `CLIENTCONTRACT_LIST`.
 */
@Component({
  selector: 'app-client-contracts',
  standalone: true,
  imports: [CommonModule, SharedModule, ModalGenerateClientContractComponent],
  templateUrl: './client-contracts.component.html',
  styleUrls: ['./client-contracts.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientContractsComponent {
  readonly companyId = input.required<string>();
  /** Nome da empresa, para o texto da modal. */
  readonly companyName = input('');

  @ViewChild('generateModal') private generateModal?: ModalGenerateClientContractComponent;

  private readonly contractsService = inject(ClientContractsHttpService);
  private readonly permissionsService = inject(PermissionsService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly toast = inject(ToastHelperService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);

  readonly contracts = signal<ClientContractListItemResponse[]>([]);
  readonly loading = signal(true);

  readonly canCreate = this.permissionSignal(Permissions.ClientContract.CREATE);
  readonly canView = this.permissionSignal(Permissions.ClientContract.VIEW);
  readonly canExportPdf = this.permissionSignal(Permissions.ClientContract.EXPORT_PDF);
  readonly canDelete = this.permissionSignal(Permissions.ClientContract.DELETE);

  /** Só liga a coluna de ações quando há alguma ação possível (o `app-table` olha o binding). */
  readonly hasRowActions = computed(
    () => this.canView() || this.canExportPdf() || this.canDelete()
  );

  readonly columnDefinitions: ColumnDefinition[] = [
    new ColumnDefinition({
      field: 'contractNumber',
      header: 'Número',
      style: { width: '150px' },
    }),
    new ColumnDefinition({ field: 'type', header: 'Tipo', template: 'type' }),
    new ColumnDefinition({ field: 'title', header: 'Título' }),
    new ColumnDefinition({ field: 'status', header: 'Situação', template: 'status' }),
    new ColumnDefinition({
      field: 'templateVersion',
      header: 'Modelo',
      template: 'templateVersion',
    }),
    new ColumnDefinition({
      field: 'outOfDefaultCount',
      header: 'Fora do padrão',
      template: 'outOfDefaultCount',
    }),
    new ColumnDefinition({ field: 'updatedAt', header: 'Atualizado em', format: 'datetime' }),
  ];

  readonly statusLabel = clientContractStatusLabel;
  readonly typeLabel = clientContractTypeLabel;
  readonly isFinalized = isClientContractFinalized;

  constructor() {
    // Recarrega quando a empresa muda (a tela de cadastro reaproveita o componente).
    effect(() => {
      const companyId = this.companyId();
      if (companyId) untracked(() => this.load());
    });
  }

  openGenerateModal(): void {
    this.generateModal?.open({ companyId: this.companyId(), companyName: this.companyName() });
  }

  /** Minuta criada: vai direto para o editor. */
  onGenerated(contract: ClientContractResponse): void {
    this.router.navigate(['/dashboard/company/contracts', contract.id]);
  }

  openEditor(contract: ClientContractListItemResponse): void {
    if (!this.canView()) return;
    this.router.navigate(['/dashboard/company/contracts', contract.id]);
  }

  rowActions(contract: ClientContractListItemResponse): MenuItem[] {
    const items: MenuItem[] = [];

    if (this.canView()) {
      items.push({
        label: 'Abrir no editor',
        icon: 'pi pi-pencil',
        route: ['/dashboard/company/contracts', contract.id],
      } as MenuItem);
    }
    if (this.canExportPdf()) {
      items.push({
        label: 'Baixar PDF',
        icon: 'pi pi-file-pdf',
        command: () => this.downloadPdf(contract),
      });
    }
    if (this.canDelete() && !this.isFinalized(contract.status)) {
      items.push({
        label: 'Excluir',
        icon: 'pi pi-trash',
        command: () => this.confirmDelete(contract),
      });
    }

    return items;
  }

  private downloadPdf(contract: ClientContractListItemResponse): void {
    this.contractsService
      .downloadContractPdf(contract.id, `${contract.contractNumber}.pdf`)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: file => saveBlobAsFile(this.document, file.blob, file.fileName),
        error: error =>
          this.toast.showToastError(
            'Não foi possível baixar o PDF',
            backendErrorMessage(error) || 'Tente de novo em instantes.'
          ),
      });
  }

  private confirmDelete(contract: ClientContractListItemResponse): void {
    this.confirmationService
      .confirm(
        `Excluir o contrato ${contract.contractNumber}?`,
        'A minuta sai da lista da empresa. O modelo e os outros contratos não mudam.',
        'danger',
        'Cancelar',
        'Excluir'
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirmed => {
        if (!confirmed) return;
        this.contractsService
          .deleteContract(contract.id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.toast.showToastSuccess('Contrato excluído', '');
              this.load();
            },
            error: error =>
              this.toast.showToastError(
                'Não foi possível excluir o contrato',
                backendErrorMessage(error) || 'Tente de novo em instantes.'
              ),
          });
      });
  }

  private load(): void {
    this.loading.set(true);
    this.contractsService
      .getContractsByCompany(this.companyId())
      .pipe(
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: list => this.contracts.set(list),
        error: error =>
          this.toast.showToastError(
            'Não foi possível carregar os contratos da empresa',
            backendErrorMessage(error) || 'Recarregue a página para tentar de novo.'
          ),
      });
  }

  private permissionSignal(permission: string) {
    return toSignal(this.permissionsService.hasPermission(permission), { initialValue: false });
  }
}
